import { apiFetch } from './api';

/**
 * OneSignal SPA helper — strict opt-in / lazy-load.
 *
 * The SDK script (cdn.onesignal.com/.../OneSignalSDK.page.js) is NEVER
 * loaded at page boot. It's injected the first time the user clicks
 * "Enable notifications" in Settings, which gives us:
 *   - No CDN dependency on every page load
 *   - No service-worker registration attempt without explicit consent
 *   - The user-gesture context required by the browser permission API
 *     stays in scope all the way through to the prompt
 *
 * After the user opts in, the OneSignal service worker
 * (/OneSignalSDKWorker.js, served from our origin) stays registered
 * and delivers pushes on its own — the SDK doesn't need to be loaded
 * on subsequent page loads to receive notifications. We only need to
 * load it again when the user wants to disable / inspect status.
 */

declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: any[];
  }
}

const SDK_SRC = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
let initInFlight: Promise<boolean> | null = null;

function detectDeviceType(): 'web-desktop' | 'web-mobile' {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'web-mobile' : 'web-desktop';
}

/**
 * Idempotent: fetches the OneSignal app id from /api/notifications/config,
 * injects the SDK script, then runs init via the deferred queue.
 *
 * Returns true if the SDK is ready to use, false if OneSignal isn't
 * configured server-side.
 */
async function loadAndInit(): Promise<boolean> {
  if (window.OneSignal) return true;
  if (initInFlight) return initInFlight;

  initInFlight = (async () => {
    const cfg = await apiFetch<{ configured: boolean; appId: string | null }>(
      '/api/notifications/config',
    );
    if (!cfg.configured || !cfg.appId) return false;

    // Resolve the LexCRM user.id once. We register it as the OneSignal
    // alias `external_id` via OneSignal.login() — which lets a single
    // user have many subscriptions (one per browser/device) all
    // fanning-out from one server-side target id. Without this every
    // browser would get a fresh OneSignal id and the server's
    // include_aliases.external_id targeting would only hit one device.
    let userId: string | null = null;
    try {
      const me = await apiFetch<{ user: { id: string } }>('/api/me');
      userId = me.user.id;
    } catch {
      // /api/me failure shouldn't break notifications init in dev/test,
      // but multi-device routing won't work without an external_id.
    }

    // Inject SDK script. The SDK exposes window.OneSignalDeferred
    // before fully initialising, so we queue our init() call there
    // and resolve when it runs.
    if (!document.querySelector('script[data-onesignal-sdk]')) {
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = SDK_SRC;
        s.async = true;
        s.dataset['onesignalSdk'] = '1';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('OneSignal SDK script failed to load'));
        document.head.appendChild(s);
      });
    }

    await new Promise<void>((resolve, reject) => {
      window.OneSignalDeferred = window.OneSignalDeferred ?? [];
      window.OneSignalDeferred.push(async (OneSignal: any) => {
        try {
          await OneSignal.init({
            appId: cfg.appId,
            allowLocalhostAsSecureOrigin: true,
            // Bind every browser/device to the same external_id alias.
            // OneSignal merges all subscriptions under this alias so
            // server-side include_aliases.external_id fan-out hits
            // every device this user is signed in on.
            ...(userId ? { externalUserId: userId } : {}),
            // OneSignal v16 ignores `serviceWorkerPath` in practice —
            // we just match the SDK's hardcoded /OneSignalSDKWorker.js
            // path on our build (vite-plugin-pwa filename). One SW,
            // one registration, no scope race.
            //
            // We control the prompt timing from /settings — no
            // OneSignal slidedown, no notify button, no auto-prompt.
            autoResubscribe: true,
            notifyButton: { enable: false },
            promptOptions: { slidedown: { enabled: false } },
          });
          // OneSignal v16 requires a separate login() call after init
          // to bind the external_id even when externalUserId was passed
          // to init — calling both is harmless and explicit-login is
          // the documented path.
          if (userId && OneSignal.login) {
            try { await OneSignal.login(userId); } catch { /* best-effort */ }
          }
          resolve();
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      });
    });

    return true;
  })();

  try {
    return await initInFlight;
  } catch (err) {
    // Reset so a follow-up click can retry from scratch.
    initInFlight = null;
    throw err;
  }
}

export async function isNotificationsEnabled(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.OneSignal) return false;
  try {
    return Boolean(window.OneSignal.User?.PushSubscription?.optedIn);
  } catch {
    return false;
  }
}

export async function browserPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Lazy-load the SDK, request browser push permission, and register the
 * resulting (oneSignalUserId, subscriptionId) pair with our backend.
 * Idempotent. Must be called from a real user gesture (the click
 * handler on the Settings "Enable notifications" button) — the
 * permission prompt API requires it.
 *
 * Returns the device id our backend assigned, or null if permission
 * was denied. Throws if OneSignal isn't configured server-side or the
 * SDK script fails to load.
 */
export async function enableNotifications(): Promise<string | null> {
  const ready = await loadAndInit();
  if (!ready) throw new Error('OneSignal not configured');
  const OneSignal = window.OneSignal;
  if (!OneSignal) throw new Error('OneSignal SDK failed to initialize');

  await OneSignal.User.PushSubscription.optIn();

  // Validate the subscription is actually live before persisting a
  // device row — otherwise we'd register a stale (token: "",
  // enabled: false) subscription that OneSignal happily accepts
  // pushes for but never delivers, looking exactly like a working
  // subscription from our DB. This was the failure mode the first
  // time around: the SPA reported "enabled" but no permission grant
  // had actually completed.
  const optedIn: boolean = Boolean(OneSignal.User.PushSubscription?.optedIn);
  const browserPerm = typeof Notification !== 'undefined' ? Notification.permission : 'default';
  if (!optedIn || browserPerm !== 'granted') {
    return null;
  }

  const oneSignalUserId: string | undefined = OneSignal.User.onesignalId;
  const subscriptionId: string | undefined = OneSignal.User.PushSubscription?.id;
  if (!oneSignalUserId) return null;

  const res = await apiFetch<{ id: string }>('/api/notifications/devices', {
    method: 'POST',
    body: JSON.stringify({
      oneSignalUserId,
      ...(subscriptionId ? { subscriptionId } : {}),
      deviceType: detectDeviceType(),
    }),
  });
  return res.id;
}

/**
 * Fires a server-side test push to every device this user has
 * registered. Confirms the OneSignal cloud + SW + delivery pipeline
 * works without waiting for a real trigger (new lead, paid invoice,
 * etc.). Throws on no_devices / onesignal_not_configured.
 *
 * `recipients` is OneSignal's "successful" — pushes forwarded to a
 * device-side gateway (FCM/Mozilla autopush). It does NOT prove the
 * browser/OS actually rendered the notification. For ground truth,
 * follow `dashboardUrl` in OneSignal's UI (per-platform stats, errors).
 * Full response is also console.logged under `[notification-test]`.
 */
export type TestNotificationResult = {
  recipients: number;
  failed: number;
  errored: number;
  converted: number;
  oneSignalId: string | null;
  dashboardUrl: string | null;
  platformDeliveryStats: Record<string, unknown> | null;
};

export async function sendTestNotification(): Promise<TestNotificationResult> {
  const res = await apiFetch<TestNotificationResult>('/api/notifications/test', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  // eslint-disable-next-line no-console
  console.log('[notification-test]', res);
  return res;
}

/**
 * Silent reconcile: when a device row already exists in our DB, the
 * SDK might be reporting different IDs (browser wiped + got a fresh
 * subscription, SW rotated, PWA reinstalled) — in which case the
 * server's stored oneSignalUserId is dead and `sendTestNotification`
 * returns recipients=0 with no obvious clue why.
 *
 * On Settings load (when deviceCount > 0) we lazy-load the SDK *without*
 * prompting (autoPrompts are off in init), read the current ids, and
 * POST them to /devices/reconcile. The server upserts the matching row
 * and deletes any other rows for this user. Logs every step under
 * `[onesignal-reconcile]` so you can trace it in DevTools.
 *
 * Returns a status the caller can show or just ignore. Never throws —
 * reconciliation is best-effort and shouldn't block the Settings page.
 */
export type ReconcileResult =
  | { status: 'not_configured' }
  | { status: 'sdk_load_failed'; error: string }
  | { status: 'no_subscription'; reason: 'no_ids' | 'not_opted_in' | 'permission_not_granted' }
  | {
      status: 'reconciled';
      kept: string;
      deleted: number;
      oneSignalUserId: string;
      subscriptionId: string;
    };

const log = (...args: unknown[]) => {
  // eslint-disable-next-line no-console
  console.log('[onesignal-reconcile]', ...args);
};

export async function reconcileDevice(): Promise<ReconcileResult> {
  log('start');
  let ready = false;
  try {
    ready = await loadAndInit();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    log('sdk load failed', msg);
    return { status: 'sdk_load_failed', error: msg };
  }
  if (!ready) {
    log('onesignal not configured server-side');
    return { status: 'not_configured' };
  }
  const OneSignal = window.OneSignal;
  if (!OneSignal) {
    log('window.OneSignal missing post-init');
    return { status: 'sdk_load_failed', error: 'OneSignal global missing post-init' };
  }

  const browserPerm = typeof Notification !== 'undefined' ? Notification.permission : 'default';
  if (browserPerm !== 'granted') {
    log('browser permission not granted', browserPerm);
    return { status: 'no_subscription', reason: 'permission_not_granted' };
  }

  // The SDK populates these asynchronously after init — poll briefly.
  let oneSignalUserId: string | undefined;
  let subscriptionId: string | undefined;
  let optedIn = false;
  for (let i = 0; i < 10; i++) {
    oneSignalUserId = OneSignal.User?.onesignalId;
    subscriptionId = OneSignal.User?.PushSubscription?.id;
    optedIn = Boolean(OneSignal.User?.PushSubscription?.optedIn);
    if (oneSignalUserId && subscriptionId) break;
    await new Promise((r) => setTimeout(r, 200));
  }
  log('SDK state', { oneSignalUserId, subscriptionId, optedIn });

  if (!optedIn) {
    log('not opted in — leaving server rows untouched');
    return { status: 'no_subscription', reason: 'not_opted_in' };
  }
  if (!oneSignalUserId || !subscriptionId) {
    log('IDs missing after poll — bailing');
    return { status: 'no_subscription', reason: 'no_ids' };
  }

  const res = await apiFetch<{
    kept: string;
    deleted: number;
    oneSignalUserId: string;
    subscriptionId: string;
  }>('/api/notifications/devices/reconcile', {
    method: 'POST',
    body: JSON.stringify({ oneSignalUserId, subscriptionId, deviceType: 'web' }),
  });
  log('reconciled', res);
  return { status: 'reconciled', ...res };
}

/**
 * Disable notifications. Two halves:
 *   - If the SDK is loaded in this session (i.e. user just opted in
 *     from this same page load), opt the local browser out so
 *     OneSignal stops trying to deliver.
 *   - Always wipe this user's devices server-side. The SDK is NOT
 *     loaded on a fresh Settings load (we lazy-load only on Enable),
 *     so without this DELETE the device row stays + the UI stays
 *     stuck in the "active on N devices" state.
 */
export async function disableNotifications(): Promise<{ deleted: number }> {
  const OneSignal = window.OneSignal;
  if (OneSignal) {
    try {
      await OneSignal.User.PushSubscription.optOut();
    } catch {
      // Swallow — the server-side wipe below is the authoritative step.
    }
  }
  return apiFetch<{ deleted: number }>('/api/notifications/devices', {
    method: 'DELETE',
  });
}
